import React from 'react';
import classNames from 'classnames';
import Decimal from 'decimal.js';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { Link } from 'react-router-dom';
import { by } from '../../utils/sort';
import { formatDateTimeStr, formatNumber } from '../../utils/i18n';
import { TYPES } from '../../constants/gasTaxDeclarations';
import {
  mapGasTaxClass,
  GAS_TAX_CLASSES,
  GAS_TAX_CLASS_NAMES,
  GAS_TAX_CLASSES_2022,
} from '../../constants/types-gastax-classes';
import { UNITS } from '../../constants/units';

const moment = extendMoment(Moment);

// const FIXED_COLUMN_WIDTH = 150;
const BASE_CLASS = 'tax-declarations';
const TABLE_CLASS = `${BASE_CLASS}__table`;

const getMeteringSiteIdColumn = t => ({
  title: t('common.table.header.id'),
  key: 'id',
  fixed: 'left',
  width: '12em',
  render: (record) => {
    const className = classNames(
      {
        [`${TABLE_CLASS}_main-column`]: !!record.meteringSiteId,
      },
    );
    return <span className={className}>{record.id}</span>;
  },
});

const getMeteringSiteNameColumn = t => ({
  title: t('common.table.header.meteringSite'),
  key: 'label',
  align: 'left',
  render: (record) => {
    const className = classNames(
      {
        [`${TABLE_CLASS}__main-column`]: !!record.meteringSiteId,
      },
    );
    const label = record.meteringSiteName;
    return <span className={className}>{label}</span>;
  },
});

const getMeteringSiteTypeColumn = t => ({
  title: t('common.table.header.type'),
  key: 'type',
  align: 'left',
  dataIndex: '',
  render: record => (record.meteringSiteType ? t(`common.label.${record.meteringSiteType}`) : ''),
});

const getRowClassName = record => classNames(
  {
    [`${TABLE_CLASS}__main-row`]: !!record.meteringSiteId,
    [`${TABLE_CLASS}__total-row`]: record.label === 'common.table.header.total',
    [`${TABLE_CLASS}__expanded-row`]: !record.meteringSiteId && record.label !== 'common.table.header.total',
  },
);

const getConsumptionsColumn = (start, end, resolution, t) => {
  if (!start || !end || !resolution) {
    return [];
  }
  const timestamps = Array
    .from(moment.range(start, end)
      .by(resolution));

  return timestamps.map((time) => {
    const title = t('declareGasTax.table.information.consumption');
    const timestamp = time.toISOString();
    return {
      title,
      key: timestamp,
      dataIndex: timestamp,
      align: 'left',
    };
  });
};

const isNumericReading = value => (
  value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
);

// Consumption data is already scoped to the requested period (the caller
// fetches exactly [start, end] for the selected month), so no period filter
// is needed here — just find the site and sum its energy readings. Readings
// arrive as numbers or numeric strings (same as elsewhere in this file, e.g.
// flattenEnergyByTimestamp) — non-numeric readings are skipped rather than
// passed to Decimal.add, which would throw on them.
//
// Returns null (not zero) when the site isn't in consumptionsData at all —
// e.g. the consumption fetch hasn't completed yet, such as on a direct/
// bookmarked navigation into the declare modal. Callers must treat null as
// "unknown, don't validate" rather than "zero consumption", or a regular
// customer's declaration gets blocked before their own data has loaded.
const getMeteringSiteConsumptionKwh = (consumptionsData, meteringSiteId) => {
  const site = (consumptionsData || []).find(item => item.meteringSiteId === meteringSiteId);
  if (!site) return null;

  const energyValues = site?.values?.energy || [];
  return energyValues.reduce(
    (total, { value }) => (isNumericReading(value) ? total.add(value) : total),
    new Decimal(0),
  );
};

// Not all classes are needed for declaration form
const getAllowedGasTaxClassNames = () => (GAS_TAX_CLASS_NAMES.filter((taxClassName) => (
  // Base Natural Gas is deducted automatically as the reminder when invoicing
  ![GAS_TAX_CLASSES_2022.PG02_NATURAL_GAS, GAS_TAX_CLASSES.PG02_NATURAL_GAS].includes(taxClassName)
)));


const getTaxClassColumns = (t) => getAllowedGasTaxClassNames().map(name => (
  {
    title: t(`declareGasTax.table.information.${name}`),
    key: name,
    dataIndex: name,
    align: 'right',
  }));

const renderDeclarationLink = (t, meteringSiteId) => (
  <Link to={`/declaregastax/${meteringSiteId}`}>
    {t('declareGasTax.table.actions.declare')}
  </Link>
);

const renderDeletionLink = (t, declarationId) => (
  <Link to={`/declaregastax/delete/${declarationId}`}>
    {t('declareGasTax.table.actions.delete')}
  </Link>
);

const getDeclarationColumn = () => ({
  title: '',
  key: 'declare',
  dataIndex: 'declare',
  align: 'right',
});

const getRowKey = (item) => {
  const {
    id,
    label,
    declarationId,
    meteringSiteId,
  } = item;

  if (declarationId) {
    return `declaration-${declarationId}`;
  }

  if (meteringSiteId) {
    return `metering-site-${meteringSiteId}`;
  }

  return `${id}-${label || 'row'}`;
};

const flattenEnergyByTimestamp = calculate => (result, {
  timestamp,
  value,
}) => ({
  ...result,
  [timestamp]: value ? formatNumber(calculate(value), 0, 3) : null,
});

const getValue = (item, key, reduceFn, initialValue) => item.values[key].reduce(
  reduceFn,
  initialValue || {
    id: item.meteringSiteId,
    label: `meteringSites.table.information.${key}`,
  },
);

const formatKWhToMWh = (value) => {
  if (typeof (value) !== 'number') return value;
  const multiplier = new Decimal(0.001);
  const calculate = val => Number(multiplier.mul(val));
  return formatNumber(calculate(value), 0, 3);
};

const formatDeclarationTotals = (totals) => {
  const formattedTotals = { ...totals };
  Object.keys(formattedTotals)
    .forEach((key) => {
      formattedTotals[key] = formatKWhToMWh(formattedTotals[key]);
    });
  return formattedTotals;
};

const getDeclarationUpdatedAt = declaration => Date.parse(declaration.createdAt || declaration.timestamp || 0) || 0;

const getLatestDeclarationPerPeriod = declarations => Object.values(
  declarations.reduce((latestDeclarations, declaration) => {
    const periodKey = declaration.period || declaration.timestamp || declaration.id;
    const currentDeclaration = latestDeclarations[periodKey];

    if (!currentDeclaration || getDeclarationUpdatedAt(declaration) >= getDeclarationUpdatedAt(currentDeclaration)) {
      return {
        ...latestDeclarations,
        [periodKey]: declaration,
      };
    }

    return latestDeclarations;
  }, {}),
);

// Returns null (not an empty amounts array) when the point has no
// declaration for this period at all — the modal must tell "nothing to
// prefill from" apart from "the latest declaration for this period was all
// zeroes", same distinction getMeteringSiteConsumptionKwh makes for null
// vs. zero consumption.
const getLatestDeclarationAmounts = (taxDeclarations, meteringPointId, period) => {
  const declarationsForPeriod = (taxDeclarations || []).filter(
    declaration => declaration.meteringPointId === meteringPointId && declaration.period === period,
  );
  const [latestDeclaration] = getLatestDeclarationPerPeriod(declarationsForPeriod);
  if (!latestDeclaration) return null;

  const amountByType = {};
  latestDeclaration.amounts.forEach(({ type, amount }) => {
    amountByType[mapGasTaxClass(type)] = amount;
  });

  return GAS_TAX_CLASS_NAMES.map(taxClassName => ({
    type: taxClassName,
    amount: typeof amountByType[taxClassName] === 'number' ? amountByType[taxClassName] : null,
  }));
};

const getDeclarationAmountTotal = (item, reduceFn, rowObject) => {
  const latestDeclarations = getLatestDeclarationPerPeriod(item.declarations);
  const typeAmounts = latestDeclarations.reduce(
    (accumulator, declaration) => declaration.amounts.reduce(
      reduceFn, accumulator,
    ),
    rowObject,
  );
  const formattedAmounts = formatDeclarationTotals(typeAmounts);
  return formattedAmounts;
};

const flattenDeclarationsByType = () => (result, {
  type,
  amount,
}) => ({
  ...result,
  [type]: result[type] ? result[type] + amount : amount,
});

const getDeclarationRows = (t, item, canDeclare) => {
  const declarationRows = [];
  const declarations = [...item.declarations]
    .sort((a, b) => getDeclarationUpdatedAt(b) - getDeclarationUpdatedAt(a));

  for (const declaration of declarations) {
    const amounts = {};
    for (const amount of declaration.amounts) {
      amounts[amount.type] = formatKWhToMWh(amount.amount);
    }
    const dateTimeStr = formatDateTimeStr(declaration.createdAt);
    const deletionLink = canDeclare ? { declare: renderDeletionLink(t, declaration.id) } : null;
    const rowData = {
      id: dateTimeStr,
      label: 'declaration',
      declarationId: declaration.id,
      ...amounts,
      ...deletionLink,
    };
    declarationRows.push(rowData);
  }
  return declarationRows;
};

const addTotal = (t, data) => {
  const stringToNumber = str => (
    typeof str === 'string'
      ? Number(str.replace(/\s/g, '')
        .replace(',', '.'))
      : 0
  );
  const format = str => formatNumber(str, 0, 3);

  return {
    id: t('common.table.header.total'),
    label: 'common.table.header.total',
    ...data
      .reduce((acc, item) => {
        const {
          id,
          label,
          children,
          ...rest
        } = item;

        Object
          .keys(rest)
          .forEach((key) => {
            // Aggregate all fields except some..
            if (!['declare', 'meteringSiteId', 'meteringSiteName', 'meteringSiteType'].includes(key)) {
              acc[key] = format(
                Decimal.add(
                  stringToNumber(acc[key]),
                  stringToNumber(rest[key]),
                ),
              );
            }
          });
        return acc;
      }, {}),
  };
};

const remap = (t, consumptionsSource, taxDeclarationsSource, unit, canDeclare = false) => {
  const multiplier = new Decimal(unit === UNITS.MWH ? 0.001 : 1);
  const calculate = value => Number(multiplier.mul(value));
  const byType = by({
    key: 'type',
    order: [TYPES.IMATRA, TYPES.EXITZONE, TYPES.BIOGAS, TYPES.CITYGATE],
  });
  const normalizedDeclarations = taxDeclarationsSource.map(declaration => ({
    ...declaration,
    amounts: declaration.amounts.map(amount => ({
      ...amount,
      type: mapGasTaxClass(amount.type),
    })),
  }));
  const mergedData = consumptionsSource.map(item => (
    {
      ...item,
      declarations: normalizedDeclarations.filter(
        declaration => declaration.meteringPointId === item.meteringSiteId,
      ),
    }
  ));
  const remappedData = mergedData
    .sort(byType)
    .map((item) => {
      const energies = getValue(item, 'energy', flattenEnergyByTimestamp(calculate), { id: item.meteringSiteId });
      const declarationAmounts = getDeclarationAmountTotal(
        item,
        flattenDeclarationsByType(),
        { id: item.meteringSiteId },
      );
      const children = getDeclarationRows(t, item, canDeclare);
      const declarationLink = canDeclare ? {
        declare: renderDeclarationLink(t, item.meteringSiteId),
      } : null;
      return {
        id: item.id,
        label: item.id,
        meteringSiteId: item.meteringSiteId,
        meteringSiteName: item.name,
        meteringSiteType: item.type,
        ...energies,
        ...declarationAmounts,
        ...declarationLink,
        children: children.length > 0 ? children : undefined,
      };
    });

  return [addTotal(t, remappedData), ...remappedData];
};

export {
  BASE_CLASS,
  TABLE_CLASS,
  getMeteringSiteIdColumn,
  getMeteringSiteNameColumn,
  getMeteringSiteTypeColumn,
  getConsumptionsColumn,
  getTaxClassColumns,
  getAllowedGasTaxClassNames,
  getMeteringSiteConsumptionKwh,
  getLatestDeclarationAmounts,
  getDeclarationColumn,
  getRowClassName,
  getRowKey,
  remap,
};
